import styled from 'styled-components';
import { useState, memo } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { FireIcon } from '@heroicons/react/24/solid';
import { fadeInUp } from '../../styles/animations';
import { GameCardHeader } from './GameCardHeader';
import { GameCardDetails } from './GameCardDetails';
import { StatusType } from '../shared/StatusBadge';

interface GameCardProps {
  filename: string;
  stage: number;
  overallResult: string;
  results: string[];
  controllerTypes: string[];
  characterIds: number[];
  costumes: number[];
  details?: any[];
  errorReason?: string;
  isHandwarmer?: boolean;
  $delay?: number;
}

const getStatusColor = (result: string, theme: any) => {
  if (result.includes('Passed') || result.includes('✅')) return theme.colors.status.success;
  if (result.includes('Failed') || result.includes('❌')) return theme.colors.status.error;
  if (result.includes('Handwarmer') || result.includes('🔥')) return theme.colors.status.warning;
  if (result.includes('Could Not Parse')) return theme.colors.status.warning;
  if (result.includes('Too Old')) return theme.colors.text.tertiary;
  return theme.colors.border;
};

const getStatusType = (result: string): StatusType => {
  if (result.includes('Passed') || result.includes('✅')) return 'passed';
  if (result.includes('Failed') || result.includes('❌')) return 'failed';
  if (result.includes('Handwarmer') || result.includes('🔥')) return 'handwarmer';
  if (result.includes('Could Not Parse')) return 'warning';
  if (result.includes('Too Old')) return 'too-old';
  return 'warning';
};

const CardContainer = styled.div<{ $statusColor: string; $delay: number }>`
  background: ${({ theme }) => theme.colors.background.secondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-left: 4px solid ${({ $statusColor }) => $statusColor};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  overflow: hidden;
  transition: all ${({ theme }) => theme.transitions.normal};
  animation: ${fadeInUp} 0.4s ease-out forwards;
  animation-delay: 0ms;
  opacity: 0;
  min-height: 120px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.medium};
    border-color: ${({ $statusColor }) => $statusColor};
  }
`;

const CardHeaderWrapper = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  cursor: pointer;
  user-select: none;
  outline: none;
  border-radius: ${({ theme }) => theme.borderRadius.lg} ${({ theme }) => theme.borderRadius.lg} 0 0;
  transition: background ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.background.elevated};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.accent.primary};
    outline-offset: -2px;
  }
`;

const ExpandButton = styled.button`
  background: ${({ theme }) => theme.colors.background.elevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.typography.sizes.small};
  font-weight: ${({ theme }) => theme.typography.weights.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  transition: all ${({ theme }) => theme.transitions.fast};
  margin-top: ${({ theme }) => theme.spacing.md};
  width: 100%;
  justify-content: center;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.accent.primary};
    color: ${({ theme }) => theme.colors.text.primary};
    border-color: ${({ theme }) => theme.colors.accent.primary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.accent.primary};
    outline-offset: 2px;
  }
`;

const DetailsContainer = styled.div<{ $isExpanded: boolean }>`
  max-height: ${({ $isExpanded }) => ($isExpanded ? '60vh' : '0')};
  overflow-y: ${({ $isExpanded }) => ($isExpanded ? 'auto' : 'hidden')};
  overflow-x: hidden;
  transition: max-height ${({ theme }) => theme.transitions.slow};
  background: ${({ theme }) => theme.colors.background.primary};
  border-top: ${({ $isExpanded, theme }) =>
    $isExpanded ? `1px solid ${theme.colors.border}` : 'none'};
  scroll-behavior: smooth;

  /* Mobile: increase max-height for vertical viewports */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    max-height: ${({ $isExpanded }) => ($isExpanded ? '70vh' : '0')};
    -webkit-overflow-scrolling: touch; /* iOS momentum scrolling */
  }

  /* Tablet: balanced height */
  @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) and (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    max-height: ${({ $isExpanded }) => ($isExpanded ? '65vh' : '0')};
  }
`;

const DetailsContent = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const HandwarmerBanner = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => `${theme.colors.status.warning}15`};
  border-bottom: 1px solid ${({ theme }) => `${theme.colors.status.warning}40`};
  color: ${({ theme }) => theme.colors.status.warning};
  font-size: ${({ theme }) => theme.typography.sizes.small};
  font-weight: ${({ theme }) => theme.typography.weights.semibold};

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

export const GameCard = memo<GameCardProps>(({
  filename,
  stage,
  overallResult,
  results,
  controllerTypes,
  characterIds,
  costumes,
  details,
  errorReason,
  isHandwarmer,
  $delay = 0,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    }
  };

  return (
    <CardContainer
      $statusColor={getStatusColor(overallResult, { colors: { status: { success: '#10b981', error: '#ef4444', warning: '#f59e0b' }, text: { tertiary: '#9ca3af' }, border: '#3a3f4b' } })}
      $delay={$delay}
      role="article"
      aria-label={`Game result for ${filename}`}
    >
      {isHandwarmer && (
        <HandwarmerBanner>
          <FireIcon />
          WARNING: Results may be unreliable (handwarmer game)
        </HandwarmerBanner>
      )}
      <CardHeaderWrapper
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-controls={`details-${filename}`}
        aria-label={`${isExpanded ? 'Hide' : 'Show'} details for ${filename}`}
      >
        <GameCardHeader
          filename={filename}
          stage={stage}
          overallResult={overallResult}
          results={results}
          controllerTypes={controllerTypes}
          characterIds={characterIds}
          costumes={costumes}
          statusType={getStatusType(overallResult)}
          isHandwarmer={isHandwarmer}
        />
        <ExpandButton
          onClick={(e) => { e.stopPropagation(); handleToggle(); }}
          aria-label={isExpanded ? 'Hide details' : 'View details'}
        >
          {isExpanded ? (
            <>
              <ChevronUpIcon /> Hide Details
            </>
          ) : (
            <>
              <ChevronDownIcon /> View Details
            </>
          )}
        </ExpandButton>
      </CardHeaderWrapper>

      <DetailsContainer
        $isExpanded={isExpanded}
        id={`details-${filename}`}
        role="region"
        aria-hidden={!isExpanded}
      >
        <DetailsContent>
          <GameCardDetails
            details={details || []}
            characterIds={characterIds}
            costumes={costumes}
            controllerTypes={controllerTypes}
            errorReason={errorReason}
          />
        </DetailsContent>
      </DetailsContainer>
    </CardContainer>
  );
});
